import type { ComponentType } from 'react';
import {
  BATTLESHIP_ID,
  CHESS_ID,
  FLEET_BY_KIND,
  OUTCOME_LABEL,
  type BattleshipView,
  type ChessOutcome,
  type ChessView,
  type GameEvent,
  type PlayerCard,
  type ShipKind,
} from '@bn/shared';
import { BattleshipMatchView } from './battleship/MatchView';
import { cellLabel } from './battleship/helpers';
import { ChessMatchView } from './chess/MatchView';

export interface GameViewProps {
  view: unknown;
  you: PlayerCard;
  opponent: PlayerCard;
  opponentOnline: boolean;
  clockOffset: number;
  onAction: (action: unknown) => Promise<void>;
}

export interface GameUi {
  View: ComponentType<GameViewProps>;
  /** Traduit un évènement du journal en une ligne lisible, ou `null` pour l'ignorer. */
  describeEvent: (event: GameEvent, nameOf: (id: string | undefined) => string) => string | null;
}

const battleshipUi: GameUi = {
  View: (props) => (
    <BattleshipMatchView
      view={props.view as BattleshipView}
      you={props.you}
      opponent={props.opponent}
      opponentOnline={props.opponentOnline}
      clockOffset={props.clockOffset}
      onAction={props.onAction}
    />
  ),

  describeEvent(event, nameOf) {
    const who = nameOf(event.actorId);
    switch (event.type) {
      case 'fleet_ready':
        return `${who} a validé sa flotte.`;
      case 'battle_started':
        return `La bataille commence — ${who} ouvre le feu.`;
      case 'shot': {
        const x = Number(event.x);
        const y = Number(event.y);
        const result =
          event.result === 'miss'
            ? 'à l’eau'
            : event.result === 'sunk'
              ? 'coulé'
              : 'touché';
        return `${who} tire en ${cellLabel(x, y)} : ${result}.`;
      }
      case 'ship_sunk': {
        const kind = event.kind as ShipKind | undefined;
        const label = kind ? FLEET_BY_KIND[kind]?.label : undefined;
        return `${who} coule ${label ? `le ${label}` : 'un navire'}.`;
      }
      case 'finished':
        return event.reason === 'resign'
          ? `Abandon — ${who} l’emporte.`
          : event.reason === 'timeout'
            ? 'Temps écoulé.'
            : event.reason === 'admin'
              ? 'Partie interrompue par un administrateur.'
              : `${who} remporte la partie.`;
      default:
        return null;
    }
  },
};

const chessUi: GameUi = {
  View: (props) => (
    <ChessMatchView
      view={props.view as ChessView}
      you={props.you}
      opponent={props.opponent}
      opponentOnline={props.opponentOnline}
      clockOffset={props.clockOffset}
      onAction={props.onAction}
    />
  ),

  describeEvent(event, nameOf) {
    const who = nameOf(event.actorId);
    switch (event.type) {
      case 'move':
        return `${who} joue ${String(event.san)}${event.check ? ' — échec' : ''}.`;
      case 'finished': {
        const outcome = event.outcome as ChessOutcome | undefined;
        const detail = outcome ? OUTCOME_LABEL[outcome] : null;
        if (event.reason === 'draw') return `Partie nulle${detail ? ` — ${detail}` : ''}.`;
        if (event.reason === 'admin') return 'Partie interrompue par un administrateur.';
        return `${who} l’emporte${detail ? ` — ${detail.toLowerCase()}` : ''}.`;
      }
      default:
        return null;
    }
  },
};

const REGISTRY: Record<string, GameUi> = {
  [BATTLESHIP_ID]: battleshipUi,
  [CHESS_ID]: chessUi,
};

export function getGameUi(gameId: string): GameUi | undefined {
  return REGISTRY[gameId];
}
